import React, { useEffect, useMemo, useState } from 'react';
import { Shield, RefreshCw, Search, UserX, UserCheck, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAllowUser, adminBlockUser, adminDeleteUser, adminListUsers } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { User } from '../types';

const statusLabel = (v: number) => {
  switch (v) {
    case 0: return { text: 'Ожидает (0)', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' };
    case 1: return { text: 'Админ (1)', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' };
    case 2: return { text: 'Разрешён (2)', cls: 'bg-green-500/15 text-green-300 border-green-500/30' };
    case 3: return { text: 'Заблокирован (3)', cls: 'bg-red-500/15 text-red-300 border-red-500/30' };
    default: return { text: `Неизвестно (${v})`, cls: 'bg-[#23262F] text-[#B1B5C3] border-[#353945]' };
  }
};

const AdminUsers: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_superuser === 1;

  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [q, setQ] = useState<string>('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListUsers(q.trim() ? q.trim() : undefined);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#B1B5C3]" />
          <div className="text-xl font-semibold">Админка</div>
        </div>
        <div className="text-[#B1B5C3]">Недостаточно прав.</div>
      </div>
    );
  }

  const doAllow = async (u: User) => {
    try {
      const updated = await adminAllowUser(u.id);
      setItems((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success('Доступ разрешён');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось разрешить доступ');
    }
  };

  const doBlock = async (u: User) => {
    try {
      const updated = await adminBlockUser(u.id);
      setItems((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success('Пользователь заблокирован');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось заблокировать');
    }
  };

  const doDelete = async (u: User) => {
    const ok = window.confirm(`Удалить пользователя #${u.id} (${u.telegram_username || u.telegram_id || 'без имени'})? Это удалит и связанные данные.`);
    if (!ok) return;
    try {
      await adminDeleteUser(u.id);
      setItems((prev) => prev.filter((x) => x.id !== u.id));
      toast.success('Пользователь удалён');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? String(detail) : 'Не удалось удалить');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-blue-400" />
          <div className="text-xl font-semibold">Админка — Пользователи</div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white hover:bg-[#2b2f3a] transition"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#B1B5C3] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск: telegram_id / username / имя"
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] text-white outline-none focus:border-blue-500"
          />
        </div>
        <button
          onClick={load}
          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white"
          disabled={loading}
        >
          Найти
        </button>
      </div>

      <div className="border border-[#23262F] rounded-xl overflow-hidden">
        {/* Desktop/tablet: таблица */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-[#141416]">
              <tr className="text-left text-[#B1B5C3]">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Telegram</th>
                <th className="px-4 py-3">Имя</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-4 text-[#B1B5C3]" colSpan={5}>Загрузка…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td className="px-4 py-4 text-[#B1B5C3]" colSpan={5}>Пользователи не найдены</td>
                </tr>
              ) : (
                sorted.map((u) => {
                  const s = statusLabel(Number(u.is_superuser || 0));
                  return (
                    <tr key={u.id} className="border-t border-[#23262F]">
                      <td className="px-4 py-3 text-white font-mono">#{u.id}</td>
                      <td className="px-4 py-3">
                        <div className="text-white">{u.telegram_username ? `@${u.telegram_username}` : (u.telegram_id || '—')}</div>
                        <div className="text-xs text-[#B1B5C3]">{u.telegram_id ? `tg_id: ${u.telegram_id}` : ''}</div>
                      </td>
                      <td className="px-4 py-3 text-white">{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md border ${s.cls}`}>{s.text}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 transition text-white disabled:opacity-50"
                            onClick={() => doAllow(u)}
                            disabled={u.is_superuser === 1}
                            title="Разрешить доступ (2)"
                          >
                            <UserCheck className="w-4 h-4" />
                            Разрешить
                          </button>
                          <button
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 transition text-white disabled:opacity-50"
                            onClick={() => doBlock(u)}
                            disabled={u.is_superuser === 1}
                            title="Заблокировать (3)"
                          >
                            <UserX className="w-4 h-4" />
                            Блок
                          </button>
                          <button
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] hover:bg-[#2b2f3a] transition text-white disabled:opacity-50"
                            onClick={() => doDelete(u)}
                            disabled={u.is_superuser === 1}
                            title="Удалить пользователя"
                          >
                            <Trash2 className="w-4 h-4" />
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <div className="md:hidden">
          {loading ? (
            <div className="p-4 text-[#B1B5C3]">Загрузка…</div>
          ) : sorted.length === 0 ? (
            <div className="p-4 text-[#B1B5C3]">Пользователи не найдены</div>
          ) : (
            <div className="p-3 space-y-3">
              {sorted.map((u) => {
                const s = statusLabel(Number(u.is_superuser || 0));
                const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
                const tgLine = u.telegram_username ? `@${u.telegram_username}` : (u.telegram_id || '—');
                return (
                  <div key={u.id} className="bg-[#181A20] border border-[#23262F] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-mono">#{u.id}</div>
                        <div className="text-white">{tgLine}</div>
                        {u.telegram_id ? <div className="text-xs text-[#B1B5C3]">tg_id: {u.telegram_id}</div> : null}
                        <div className="text-sm text-[#B1B5C3] mt-1">{name}</div>
                      </div>
                      <span className={`h-fit inline-flex items-center px-2 py-1 rounded-md border ${s.cls}`}>{s.text}</span>
                    </div>

                    <div className="grid grid-cols-1 gap-2 mt-4">
                      <button
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 transition text-white disabled:opacity-50"
                        onClick={() => doAllow(u)}
                        disabled={u.is_superuser === 1}
                      >
                        <UserCheck className="w-4 h-4" />
                        Разрешить (2)
                      </button>
                      <button
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 transition text-white disabled:opacity-50"
                        onClick={() => doBlock(u)}
                        disabled={u.is_superuser === 1}
                      >
                        <UserX className="w-4 h-4" />
                        Заблокировать (3)
                      </button>
                      <button
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#23262F] border border-[#353945] hover:bg-[#2b2f3a] transition text-white disabled:opacity-50"
                        onClick={() => doDelete(u)}
                        disabled={u.is_superuser === 1}
                      >
                        <Trash2 className="w-4 h-4" />
                        Удалить
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;

